import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import { CreditCard, Home, Scissors, Info } from 'lucide-react';

export default function PricingPage() {
  const medicalAids = [
    "Discovery Health Medical Scheme",
    "Momentum Health Medical Scheme",
    "Profmed Medical Scheme",
    "Liberty Medical Scheme",
    "SAB Medical Scheme",
    "AECI Medical Aid Society (Medscheme)",
    "Barloworld Medical Scheme",
    "Bonitas",
    "Fedhealth",
    "Government Employees Medical Scheme (GEMS)",
    "Horizon Medical Scheme",
    "MBMed Medical Aid Fund",
    "Medshield",
    "Parmed Medical Aid Scheme",
    "SABC Medical Scheme",
    "SAMWUMED",
    "South African Police Service (POLMED)"
  ];

  return (
    <Layout>
      {/* Hero Section - Turquoise */}
      <section className="py-16 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 animate-fade-in" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Payment Information
          </h1>
          <p className="text-lg text-slate-800 max-w-2xl">
            We charge medical aid rates and accept medical aid payments. Cash payments are also accepted upfront.
          </p>
        </div>
      </section>

      {/* Pricing Cards - White */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Cash Paying Patients
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* At the rooms */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 card-hover animate-fade-in">
              <div className="w-14 h-14 rounded-full bg-[#f0fdfd] flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-[#0e7490]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                At the Rooms
              </h3>
              <p className="text-sm text-slate-500 mb-4">Initial consultation (30-45 minutes)</p>
              <p className="text-3xl font-bold text-[#0e7490]">R750.00</p>
            </div>

            {/* Home visits */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 card-hover animate-fade-in animate-delay-100">
              <div className="w-14 h-14 rounded-full bg-[#f0fdfd] flex items-center justify-center mb-4">
                <Home className="w-7 h-7 text-[#0e7490]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Home Visits
              </h3>
              <p className="text-sm text-slate-500 mb-4">Initial or follow up (45 min - 1 hr)</p>
              <p className="text-3xl font-bold text-[#0e7490]">R900.00</p>
            </div>

            {/* Sports massage */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 card-hover animate-fade-in animate-delay-200">
              <div className="w-14 h-14 rounded-full bg-[#f0fdfd] flex items-center justify-center mb-4">
                <Scissors className="w-7 h-7 text-[#0e7490]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Sports Massage
              </h3>
              <p className="text-sm text-slate-500 mb-4">30 minutes session</p>
              <p className="text-3xl font-bold text-[#0e7490]">R620.00</p>
            </div>
          </div>

          {/* Note */}
          <div className="bg-[#f0fdfd] rounded-2xl p-6 mb-12 flex items-start gap-4">
            <Info className="w-6 h-6 text-[#0e7490] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-slate-700">
                Please feel free to contact me should you have any questions or concerns about our prices.
              </p>
              <p className="text-slate-600 mt-2 font-medium">
                It is the patient's responsibility to ensure their account is settled in full.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Aids - Turquoise */}
      <section className="py-16 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Medical Aids We Accept
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {medicalAids.map((aid, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-4 text-center shadow-sm border border-slate-100 animate-fade-in"
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <p className="text-sm text-slate-700">{aid}</p>
              </div>
            ))}
          </div>

          <p className="text-sm text-slate-700 mt-6 text-center">
            * Note: Some Discovery Health plans don't have physio benefits. Please check with your provider.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#5ce1e6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Book?
          </h2>
          <p className="text-slate-700 mb-6">
            Schedule your appointment today
          </p>
          <Link 
            to="/book" 
            className="inline-flex items-center justify-center px-8 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-colors"
            data-testid="pricing-book-btn"
          >
            Book Appointment
          </Link>
        </div>
      </section>
    </Layout>
  );
}
